<!--
  AppBar.vue
  Shared application bar component, extracted from App.vue.
  Desktop: hamburger toggle for rail drawer.
  Mobile: shows view icon/thumbnail + title (replaces per-view toolbars).
  Always shows the Add URL button and mobile dots menu when logged in.
-->
<template>
  <!-- Use the theme's surface color (opaque) instead of "transparent".
       A transparent bar leaves the notch area see-through in PWA mode
       on iPhone, which looks broken. Notch handling (taller bar +
       extra v-main offset) lives in global CSS in App.vue. -->
  <v-app-bar flat border="b" density="comfortable">
    <!-- Global page-loading indicator: a single non-blocking indeterminate bar
         pinned to the bottom edge of the app bar. Replaces the old blocking
         loading overlays (which froze the page when stuck). Driven by the
         shared `busy` state set by each view while it fetches. -->
    <v-progress-linear
      v-if="busy"
      indeterminate
      color="primary"
      height="3"
      style="position: absolute; bottom: 0; left: 0; right: 0"
    />
    <template v-slot:prepend>
      <!-- Desktop: hamburger opens drawer, close icon collapses it back to rail -->
      <v-btn
        v-if="loggedIn && mdAndUp"
        :icon="navRail ? 'mdi-menu' : 'mdi-close'"
        @click="navRail = !navRail"
      ></v-btn>
      <!-- Mobile: show podcast thumbnail (episodes view) or page icon -->
      <template v-else-if="loggedIn && !mdAndUp">
        <v-avatar v-if="viewThumbnail" rounded size="28" class="ml-2">
          <v-img :src="viewThumbnail"></v-img>
        </v-avatar>
        <v-icon
          v-else-if="pageIcon"
          :icon="pageIcon"
          style="margin-left: 4px"
        ></v-icon>
      </template>
      <!-- Not logged in: just show page icon -->
      <v-icon
        v-else-if="pageIcon"
        :icon="pageIcon"
        style="margin-left: 4px"
      ></v-icon>
    </template>
    <!-- Not logged in: show page title (e.g. login page) -->
    <v-toolbar-title
      v-if="!loggedIn"
      style="flex: none; margin-inline-start: 4px"
      >{{ pageTitle }}
    </v-toolbar-title>
    <!-- Desktop: show view-specific title (e.g. podcast name) or fallback to route-based pageTitle -->
    <v-toolbar-title
      v-else-if="mdAndUp"
      style="flex: none; margin-inline-start: 4px"
      >{{ viewTitle || pageTitle }}</v-toolbar-title
    >
    <!-- Mobile: a plain <div> avoids v-toolbar-title's built-in truncation
         (Vuetify's grid-based app bar clips overflow on the title area itself,
         so even !important overrides on v-toolbar-title can't prevent it).
         text-title-medium matches the Material Design 3 title-medium scale.
         flex 1 / min-width 0 / overflow-wrap let it wrap so the dots menu
         always stays visible. -->
    <div v-else class="text-title-medium mobile-title">
      {{ viewTitle || pageTitle }}
    </div>
    <!-- Count chip: shows the number of items in the current view (episodes,
         podcasts, etc.). Turns WARNING/orange with a filter icon while a
         search/filter is active — the count then reflects the FILTERED list,
         and the color makes the narrowed state impossible to miss. -->
    <v-chip
      v-if="loggedIn && pageCount != null"
      variant="outlined"
      size="x-small"
      class="mx-2"
      :color="viewMenuSearchActive ? 'warning' : undefined"
      :prepend-icon="viewMenuSearchActive ? 'mdi-filter' : undefined"
      >{{ pageCount }}</v-chip
    >
    <v-spacer></v-spacer>
    <!-- Status-page queue indicators: per-queue active-job counts, published by
         StatusView via the shared `statusActivity` state. Shown only while the
         Status page is open (it clears the state on unmount). -->
    <template v-if="loggedIn && statusActivity">
      <!-- Video queue: determinate ring shows average download progress -->
      <v-progress-circular
        v-if="statusActivity.video"
        :model-value="statusActivity.videoProgress"
        :size="24"
        :width="3"
        color="purple"
        class="mr-2"
        :aria-label="`${statusActivity.video} video downloads`"
      >
        <span class="download-count">{{ statusActivity.video }}</span>
      </v-progress-circular>
      <!-- Metadata queue: indeterminate (no numeric progress) -->
      <v-progress-circular
        v-if="statusActivity.metadata"
        indeterminate
        :size="24"
        :width="3"
        color="info"
        class="mr-2"
        :aria-label="`${statusActivity.metadata} metadata jobs`"
      >
        <span class="download-count">{{ statusActivity.metadata }}</span>
      </v-progress-circular>
      <!-- Monitor queue: indeterminate -->
      <v-progress-circular
        v-if="statusActivity.monitor"
        indeterminate
        :size="24"
        :width="3"
        color="success"
        class="mr-2"
        :aria-label="`${statusActivity.monitor} monitor checks`"
      >
        <span class="download-count">{{ statusActivity.monitor }}</span>
      </v-progress-circular>
      <!-- AI summary queue: indeterminate (Groq call, no numeric progress) -->
      <v-progress-circular
        v-if="statusActivity.summary"
        indeterminate
        :size="24"
        :width="3"
        color="teal"
        class="mr-2"
        :aria-label="`${statusActivity.summary} summaries generating`"
      >
        <span class="download-count">{{ statusActivity.summary }}</span>
      </v-progress-circular>
    </template>
    <!-- Downloading indicator: spinner with count badge of episodes being cached/downloading.
         Only visible when downloadingCount > 0. Persists across views so the user
         can navigate away while caching is in progress. Appears left of the other
         right-side icons so it doesn't get pushed off-screen on small screens. -->
    <v-btn
      v-if="loggedIn && downloadingCount > 0"
      icon
      :aria-label="`${downloadingCount} episode${downloadingCount > 1 ? 's' : ''} downloading`"
    >
      <v-progress-circular :size="24" :width="3" indeterminate color="warning">
        <template v-slot:default>
          <span class="download-count">{{ downloadingCount }}</span>
        </template>
      </v-progress-circular>
    </v-btn>
    <!-- View-specific action button 2 (e.g. Filter on admin-users page) -->
    <v-btn
      v-if="loggedIn && viewAction2Callback"
      :icon="viewAction2Icon"
      :color="viewAction2Color || undefined"
      @click="viewAction2Callback()"
    ></v-btn>
    <!-- View-specific action button (e.g. Add User on admin-users page) -->
    <v-btn
      v-if="loggedIn && viewActionCallback"
      :icon="viewActionIcon"
      @click="viewActionCallback()"
    ></v-btn>
    <!-- Clear-filter funnel: shown directly in the app bar (not buried in the
         dots menu) whenever the current view has an active search/filter and has
         registered a clear callback. One tap cancels the filter. Uses the default
         icon color (no warning/orange tint). The tooltip (hover / long-press)
         reminds WHAT was searched — query text and/or status filter — via the
         live description getter the view registered (viewFilterDescription). -->
    <v-tooltip
      v-if="loggedIn && viewMenuSearchActive && viewClearFilterCallback"
      :text="filterTooltip"
      location="bottom"
    >
      <template #activator="{ props: tipProps }">
        <v-btn
          v-bind="tipProps"
          icon="mdi-filter-remove"
          aria-label="Clear filter"
          @click="viewClearFilterCallback()"
        ></v-btn>
      </template>
    </v-tooltip>
    <!-- Dots menu triggers the current view's context menu (registered via viewMenuCallback) -->
    <v-btn
      v-if="loggedIn && viewMenuCallback"
      icon="mdi-dots-vertical"
      @click="viewMenuCallback()"
    ></v-btn>
  </v-app-bar>
</template>

<script setup>
/**
 * AppBar.vue - Script Section
 *
 * Extracted from App.vue. Uses composables for all shared state:
 * - useAppMenu for navigation drawer, add URL dialog, view-specific callbacks
 * - useLogin for auth state
 * - useDisplay for responsive breakpoints
 * - useRoute for route-based title/icon mapping
 */

import { computed } from "vue";
import { useRoute } from "vue-router";
import { useDisplay } from "vuetify";
import { useLogin } from "@/composables/useLogin.js";
import { useAppMenu } from "@/composables/useAppMenu.js";

const route = useRoute();
const { mdAndUp } = useDisplay();
const { loggedIn } = useLogin();

// Shared state from useAppMenu composable
const {
  navRail,
  viewMenuCallback,
  viewMenuSearchActive,
  viewClearFilterCallback,
  viewFilterDescription,
  viewActionCallback,
  viewActionIcon,
  viewAction2Callback,
  viewAction2Icon,
  viewAction2Color,
  viewTitle,
  viewThumbnail,
  downloadingCount,
  pageCount,
  busy,
  statusActivity,
} = useAppMenu();

/**
 * filterTooltip - Tooltip text for the clear-filter funnel: the active view's
 * registered filter description (e.g. `Filtered: “star” · Starred`) plus the
 * action hint. Calling the registered getter INSIDE this computed makes the
 * tooltip live-reactive to the underlying search refs the getter reads.
 */
const filterTooltip = computed(() => {
  // The registered getter (a function) — absent when the view registered none
  const describe = viewFilterDescription.value;
  const description = typeof describe === "function" ? describe() : null;
  return description ? `${description} — tap to clear` : "Clear filter";
});

/**
 * pageTitle - Computed: maps route names to human-readable page titles
 * for display in the app bar. Falls back to "FYT" for unknown routes.
 */
const pageTitle = computed(() => {
  const titles = {
    "list-podcasts": "Podcasts",
    "list-latest": "Latest",
    playlist: "Playlist",
    "list-episodes": "Episodes",
    search: "Search episodes",
    history: "Play History",
    "add-podcast": "Add Podcast",
    "edit-podcast": "Edit Podcast",
    "saved-searches": "Saved searches",
    sideload: "Upload a file or a text",
    settings: "Settings",
    status: "Status",
    "admin-status": "Status — All Users",
    admin: "Admin API",
    "admin-users": "Users",
    "admin-settings": "Server Settings",
    "admin-architecture": "Architecture",
  };
  return titles[route.name] || "FYT";
});

/**
 * pageIcon - Computed: maps route names to Material Design icon names
 * for display in the app bar prepend slot on mobile.
 */
const pageIcon = computed(() => {
  const icons = {
    "list-podcasts": "mdi-podcast",
    "list-latest": "mdi-history",
    playlist: "mdi-playlist-music-outline",
    "list-episodes": "mdi-rss",
    search: "mdi-magnify",
    history: "mdi-play-box-multiple-outline",
    "add-podcast": "mdi-plus",
    "edit-podcast": "mdi-pencil",
    "saved-searches": "mdi-rss",
    sideload: "mdi-file-upload-outline",
    settings: "mdi-account-cog",
    status: "mdi-clock",
    "admin-status": "mdi-account-multiple-outline",
    admin: "mdi-api",
    "admin-users": "mdi-account-group",
    "admin-settings": "mdi-tune",
    "admin-architecture": "mdi-file-document-outline",
  };
  return icons[route.name] || null;
});
</script>

<style scoped>
/* ═══ DOWNLOAD COUNT SPINNER: number inside progress circle ═══ */
.download-count {
  font-size: 12px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  text-align: center;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

/* ═══ MOBILE TITLE: plain <div> avoids v-toolbar-title's truncation ═══
   Vuetify's v-app-bar grid constrains the title area, and v-toolbar-title
   applies its own overflow:hidden / text-overflow:ellipsis / white-space:nowrap.
   By using a plain div we sidestep that entirely so the text can wrap.
   No flex:1 — that would fight with v-spacer for width. Instead the spacer
   pushes buttons right, and if the title is too long it wraps naturally.
   Reduced line-height keeps wrapped multi-line titles compact in the app bar. */
.mobile-title {
  white-space: normal;
  overflow-wrap: break-word;
  margin-inline-start: 4px;
  line-height: 1.2;
}
</style>
